import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { useCurrentUser } from '../../contexts/CurrentUser';
import { Loader } from '../../components/Loader/Loader';
import { getWorkspaceRoles } from '../../utils/artifactsUi';
import { Grant, listGrants, revokeGrant } from '../../utils/oauth';
import styles from './ConnectedAppsPage.module.scss';

/**
 * The confirmation for a destructive, immediate action.
 *
 * Activating Revoke unmounts that button and mounts these two, which destroys
 * focus and announces nothing — leaving a screen-reader user with a "Confirm"
 * button and no idea what it confirms. As an alertdialog it is announced with
 * its question when focus lands on Cancel, which is also the safe default.
 */
function RevokeConfirm({
  grant,
  busy,
  onCancel,
  onConfirm,
}: {
  grant: Grant;
  busy: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  const { t } = useTranslation();
  const cancelRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    cancelRef.current?.focus();
  }, []);

  const titleId = `revoke-title-${grant.id}`;
  const descriptionId = `revoke-description-${grant.id}`;

  return (
    <div
      className={styles.confirm}
      role="alertdialog"
      aria-labelledby={titleId}
      aria-describedby={descriptionId}
    >
      <p className={styles.confirmText} id={titleId}>
        {/* Interpolated rather than concatenated: languages do not agree on where
            the name goes. Escaping is off because this lands in a React text node,
            which escapes already — leaving it on would render an application
            called "Bar & Co" as "Bar &amp; Co". */}
        {t('Disconnect {{name}}?', {
          name: grant.client_name,
          interpolation: { escapeValue: false },
        })}
      </p>
      {/* The consequence, for the reader who cannot see that this row is a live
          integration. Hidden visually because the page header already says it. */}
      <p className={styles.srOnly} id={descriptionId}>
        {t('This disconnects the application immediately. It would have to be authorized again.')}
      </p>
      <button
        type="button"
        className="secondary"
        ref={cancelRef}
        disabled={busy}
        onClick={onCancel}
      >
        {t('Cancel')}
      </button>
      <button type="button" className="btn-danger" disabled={busy} onClick={onConfirm}>
        {t('Confirm')}
      </button>
    </div>
  );
}

function formatConnectedAt(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }
  return date.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

/**
 * Applications the user has connected over OAuth, with a revoke action.
 *
 * Revoking is destructive and immediate — it breaks a working integration on
 * the user's other machine — so it takes a second, named confirmation rather
 * than firing on the first click.
 */
export function ConnectedAppsPage() {
  const { t } = useTranslation();

  // The page is reachable by URL, so the entitlement is enforced here and not
  // only on the menu entry. It also gates the fetch: a role-less 403 from
  // API.fetch semantics would be read as an expired session.
  const [currentUser] = useCurrentUser();
  const hasOAuthAccess = Boolean(getWorkspaceRoles(currentUser)?.includes('oauth:access'));

  const [grants, setGrants] = useState<Grant[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [confirmingId, setConfirmingId] = useState<number | null>(null);
  const [revokingId, setRevokingId] = useState<number | null>(null);
  const [revokeFailed, setRevokeFailed] = useState(false);
  const headingRef = useRef<HTMLHeadingElement>(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (!hasOAuthAccess) return;

    let cancelled = false;

    listGrants()
      .then((data) => {
        if (cancelled) return;
        setGrants(data);
        setLoading(false);
      })
      .catch(() => {
        if (cancelled) return;
        setLoadFailed(true);
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [hasOAuthAccess]);

  const confirmRevoke = useCallback(async (grant: Grant) => {
    setRevokeFailed(false);
    setRevokingId(grant.id);

    try {
      await revokeGrant(grant.id);
      // The API revokes every live grant the user holds for this application,
      // not only the row that was clicked: re-consenting creates an additional
      // grant rather than updating the existing one. Dropping just this row
      // would keep the siblings on screen until a reload, even though their
      // tokens are already dead.
      setGrants((current) => current.filter((g) => g.client_id !== grant.client_id));
      setConfirmingId(null);
      // The row holding focus is about to disappear. Park focus on the heading
      // rather than dropping the user back on <body> with no idea what happened.
      headingRef.current?.focus();

      // Then trust the server over our guess: whatever it now reports as live
      // is what the page shows. A failed refresh keeps the optimistic list —
      // the revoke itself succeeded, and an error here would say otherwise.
      try {
        const fresh = await listGrants();
        if (mountedRef.current) setGrants(fresh);
      } catch {
        // Optimistic state stands.
      }
    } catch {
      // Keep the row: the integration is still live, and saying otherwise
      // would be a lie the user acts on.
      setRevokeFailed(true);
    } finally {
      setRevokingId(null);
    }
  }, []);

  // `currentUser` is null until /users/me resolves; redirecting then would bounce
  // an entitled user off their own page.
  if (currentUser && !hasOAuthAccess) return <Navigate to="/" replace />;

  if (loading) {
    // The Loader is purely visual; the live region is what a screen reader hears.
    return (
      <div role="status">
        <span className={styles.srOnly}>{t('Loading connected applications')}</span>
        <Loader />
      </div>
    );
  }

  return (
    <div>
      <div className={styles.header}>
        <h1 className={styles.title} tabIndex={-1} ref={headingRef}>
          {t('Connected applications')}
        </h1>
        <p className={styles.subtitle}>
          {t(
            'Applications you have authorized to read data from this workspace on your behalf. Revoking access disconnects the application immediately.',
          )}
        </p>
      </div>

      {loadFailed ? (
        <p className={styles.error} role="alert">
          {t('We could not load your connected applications. Please try again.')}
        </p>
      ) : null}

      {revokeFailed ? (
        <p className={styles.error} role="alert">
          {t('We could not revoke that access. Please try again.')}
        </p>
      ) : null}

      {!loadFailed && grants.length === 0 ? (
        <p className={styles.empty}>{t('No connected applications yet.')}</p>
      ) : null}

      {grants.length > 0 ? (
        <ul className={styles.list}>
          {grants.map((grant) => (
            <li className={styles.row} key={grant.id}>
              <div className={styles.rowMain}>
                <p className={styles.clientName}>{grant.client_name}</p>
                <p className={styles.meta}>
                  {t('Connected {{date}}', { date: formatConnectedAt(grant.created_at) })}
                </p>
                {grant.scopes.length > 0 ? (
                  <div className={styles.scopes}>
                    {grant.scopes.map((scope) => (
                      <span className={styles.scope} key={scope}>
                        {scope}
                      </span>
                    ))}
                  </div>
                ) : (
                  <span className={styles.noScopes}>{t('No permissions granted')}</span>
                )}
              </div>

              {confirmingId === grant.id ? (
                <RevokeConfirm
                  grant={grant}
                  busy={revokingId === grant.id}
                  onCancel={() => setConfirmingId(null)}
                  onConfirm={() => confirmRevoke(grant)}
                />
              ) : (
                <button
                  type="button"
                  className="secondary"
                  onClick={() => {
                    setRevokeFailed(false);
                    setConfirmingId(grant.id);
                  }}
                >
                  {t('Revoke')}
                </button>
              )}
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}
